import React, { useContext, useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  Button,
  Divider,
  Icon,
  Space,
  Text,
  Color,
  Flex,
} from '@bb/elements-web';
import { orgCount } from 'api/UserController';
import { renderCreditCardIcon } from '../../components/ManageCard/CreditCardIcons';
import HomeIcon from './icons/HomeIcon';
import UpcomingChangesIcons from './icons/UpcomingChangesIcons';
import { ManageCardContext } from '../../components/ManageCard/ManageCardContext';
import AnsweringServiceContext from '../AnsweringServicePage/AnsweringServiceContext';
import type { PaymentConfirmationBillingInfo } from './types/paymentConfirmationTypes';

type ServiceType = 'tier2' | 'liveConcierge';

type PaymentConfirmationContentProps = {
  billingInfo: PaymentConfirmationBillingInfo | undefined;
  handleAddAddressClick: () => void;
  serviceType: ServiceType;
};

/**
 * Formats an expiry date string to "MMM YYYY" format (e.g., "Sep 2028")
 * Handles common formats: "MM/YY", "MM/YYYY", "MMYY", "MMYYYY", "YYYY-MM"
 */
export const formatExpiryDate = (expiryDate?: string): string => {
  if (!expiryDate) return '';

  let month: number;
  let year: number;

  // Handle ISO format (YYYY-MM)
  if (expiryDate.includes('-')) {
    const [yearStr, monthStr] = expiryDate.split('-');
    month = parseInt(monthStr, 10);
    year = parseInt(yearStr, 10);
  }
  // Handle slash format (MM/YY or MM/YYYY)
  else if (expiryDate.includes('/')) {
    const [monthStr, yearStr] = expiryDate.split('/');
    month = parseInt(monthStr, 10);
    year = parseInt(yearStr, 10);
    // Convert 2-digit year to 4-digit
    if (year < 100) {
      year += 2000;
    }
  }
  // Handle concatenated format (MMYY or MMYYYY)
  else {
    month = parseInt(expiryDate.substring(0, 2), 10);
    const yearStr = expiryDate.substring(2);
    year = parseInt(yearStr, 10);
    if (year < 100) {
      year += 2000;
    }
  }

  if (Number.isNaN(month) || Number.isNaN(year) || month < 1 || month > 12) {
    return expiryDate;
  }

  const date = new Date(year, month - 1);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    year: 'numeric',
  }).format(date);
};

/**
 * The page that shows during the Answering Service subscription flow
 * confirming the monetary total with the user and allowing them to make
 * any billing changes they want.
 */
const PaymentConfirmationContent: React.FC<PaymentConfirmationContentProps> = ({
  billingInfo,
  handleAddAddressClick,
  serviceType,
}) => {
  const { setModalVisible: setJPMCModalVisible } =
    useContext(ManageCardContext);

  // Only populate the following values from the context when AnsweringServiceContext
  // is rendered around this element. (i.e. serviceType === 'liveConcierge').
  // Otherwise populate them with blank valid data to avoid eslint errors.
  const answeringServiceContext = useContext(AnsweringServiceContext);
  const enabledListingsCount =
    answeringServiceContext?.enabledListingsCount ?? 0;
  const viewingLevel = answeringServiceContext?.viewingLevel ?? 'org';

  /** The number of users in this brokerage. */
  const [agentCount, setAgentCount] = useState(0);

  const [billableAgentCount, setBillableAgentCount] = useState<
    number | undefined
  >(undefined);

  // TODO: Uncomment this when branch KB-1271_brokerage-plan-upragde-modal is merged in
  // const fetchBillableAgentCount = async () => {
  //   try {
  //     const { usersCount } = await UserController.findBillableAgentsForPlan();
  //     setBillableAgentCount(usersCount);
  //   } catch (error) {
  //     message.error('Failed to fetch billable agent count. Please try again.');
  //   }
  // };

  // useEffect(() => {
  //   fetchBillableAgentCount();
  // }, []);

  useEffect(() => {
    /** Method to grab the number of active agents in this brokerage. */
    const fetchAgentCount = async () => {
      if (viewingLevel === 'org') {
        const { active } = await orgCount();
        setAgentCount(active);
      }
    };
    fetchAgentCount();
  }, [viewingLevel]);

  const formattedAddress = () => {
    if (!billingInfo) return '';

    const { address1, address2, city, state, zipCode, country } = billingInfo;

    return `${address1}${address2 ? `, ${address2}` : ''}, ${city}, ${state}, ${zipCode}, ${country}`;
  };

  const billingInfoHasContactInfo = !!(
    billingInfo &&
    billingInfo.address1 &&
    billingInfo.city &&
    billingInfo.state &&
    billingInfo.zipCode &&
    billingInfo.country &&
    billingInfo.email
  );

  const billingInfoHasPaymentInfo = !!(
    billingInfo &&
    billingInfo.cardholderName &&
    billingInfo.ccExpiryDate &&
    billingInfo.ccNumber &&
    billingInfo.ccType &&
    billingInfo.maskedCCNumber
  );

  return (
    <Space direction="vertical" size={40}>
      {
        /** Show the Org Plan icon */
        serviceType === 'tier2' && (
          <StyledContainer $flexDirection="row">
            <HomeIcon />
            <StyledContainer $justifyContent="center" $gap={4}>
              <SubTitle style={{ display: 'block' }}>Brokerage Plan</SubTitle>
              <Text style={{ display: 'block', fontSize: 16 }} color="grey">
                {billableAgentCount !== undefined
                  ? `${billableAgentCount} agents`
                  : `\u00A0`}
              </Text>
            </StyledContainer>
          </StyledContainer>
        )
      }
      {
        /** Show the Answering Service icon */
        serviceType === 'liveConcierge' && (
          <ServiceListingsContainer>
            <IconCircle>
              <Icon
                icon="liveConcierge"
                size={26}
                color="primary"
                style={{ display: 'flex' }}
              />
            </IconCircle>
            <TextContainer>
              <Text size="large" thickness="semiBold">
                Live Concierge
              </Text>
              <Text color="grey" size="large">
                {viewingLevel === 'org'
                  ? `${agentCount} agents`
                  : `${enabledListingsCount} listings`}
              </Text>
            </TextContainer>
          </ServiceListingsContainer>
        )
      }

      <StyledContainer $fontSize={16}>
        <SubTitle>Upcoming Changes</SubTitle>
        <StyledContainer $flexDirection="row">
          <UpcomingChangesIcons />
          <StyledUpcomingChangesContentContainer>
            <StyledUpcomingChangesContentItemContainer>
              <div style={{ flexGrow: 1 }}>
                <Text thickness="bold" size="large">
                  Today
                </Text>
                <Text style={{ display: 'block' }} color="grey" size="large">
                  23 days prorated fee
                </Text>
              </div>
              <Text thickness="bold" size="large">
                $45
              </Text>
            </StyledUpcomingChangesContentItemContainer>

            <StyledUpcomingChangesContentItemContainer>
              <div style={{ flexGrow: 1 }}>
                <Text
                  thickness="bold"
                  style={{ display: 'block' }}
                  size="large"
                >
                  Starting Nov 27
                </Text>
              </div>
              <div style={{ flexGrow: 1, textAlign: 'right' }}>
                <Text thickness="bold" size="large">
                  $36/mo
                </Text>
                <Text
                  style={{ display: 'block', textAlign: 'right' }}
                  color="grey"
                >
                  + applicable taxes
                </Text>
              </div>
            </StyledUpcomingChangesContentItemContainer>
          </StyledUpcomingChangesContentContainer>
        </StyledContainer>
      </StyledContainer>

      <StyledContainer>
        <Flex justify="space-between" align="center">
          <SubTitle style={{ display: 'block' }}>Contact & Address</SubTitle>
          {billingInfoHasContactInfo && (
            <ActionButton
              onClick={handleAddAddressClick}
              dataCy="edit-email-btn"
              minWidth="auto"
              style={{ padding: '4px 16px' }}
            >
              Edit
            </ActionButton>
          )}
        </Flex>
        {billingInfoHasContactInfo ? (
          <>
            <ContactInfoItemContainer>
              <Text style={{ display: 'block' }} color="grey" size="large">
                Email
              </Text>
              <Text size="large">{billingInfo?.email || `\u00A0`}</Text>
            </ContactInfoItemContainer>

            <ContactInfoItemContainer>
              <Text style={{ display: 'block' }} color="grey" size="large">
                Address
              </Text>
              <Text size="large">{formattedAddress() || `\u00A0`}</Text>
            </ContactInfoItemContainer>
          </>
        ) : (
          <ActionButton
            onClick={handleAddAddressClick}
            type="secondary"
            dataCy="add-btn"
            minWidth="auto"
            style={{
              paddingLeft: '16px',
              paddingRight: '16px',
              width: 'fit-content',
            }}
          >
            Add
          </ActionButton>
        )}
      </StyledContainer>

      <StyledContainer>
        <SubTitle style={{ display: 'block' }}>Payment Details</SubTitle>
        {billingInfoHasPaymentInfo ? (
          <StyledContainer $flexDirection="row" $alignItems="center" $gap={24}>
            {renderCreditCardIcon(billingInfo.ccType?.toLowerCase() || '', 36)}
            <StyledContainer style={{ flex: '1 0 0' }} $gap={8}>
              <SubTitle>
                {billingInfo.ccType} ending in{' '}
                {billingInfo.maskedCCNumber.slice(-4)}
              </SubTitle>
              <Text color="grey" size="large">
                Expires {formatExpiryDate(billingInfo?.ccExpiryDate)}
              </Text>
            </StyledContainer>
            <ActionButton
              onClick={() => setJPMCModalVisible(true)}
              dataCy="edit-btn"
              minWidth="auto"
              style={{ padding: '4px 16px' }}
            >
              Update
            </ActionButton>
          </StyledContainer>
        ) : (
          <StyledContainer $alignItems="flex-start" $gap={8}>
            <StyledPaymentButton
              size="middle"
              type="secondary"
              onClick={() => setJPMCModalVisible(true)}
              dataCy="add-cc-btn"
            >
              Add credit card
            </StyledPaymentButton>
            <Text color="grey" size="large" style={{ display: 'block' }}>
              Powered by JPMC
            </Text>
          </StyledContainer>
        )}
      </StyledContainer>

      <div>
        <Text color="grey" style={{ fontSize: 16, display: 'block' }}>
          You will be paying a prorated fee today to activate the service
          immediately. Your next invoice will start on{' '}
          <span style={{ fontWeight: 'bold' }}>[Billing Date]</span> and will
          renew automatically every month. Cancel anytime in Subscription
          Settings. By continuing, you agree to{' '}
          <StyledTOCLink
            href="https://www.brokerbay.com/supraone-user-terms-and-conditions"
            target="_blank"
            rel="noreferrer"
          >
            Supra’s Terms of Service
          </StyledTOCLink>
          .
        </Text>
        <Divider />
      </div>
    </Space>
  );
};

const StyledTOCLink = styled.a`
  font-family: inherit;
  font-weight: bold;

  && {
    text-decoration: underline;
    color: ${Color.SUVA_GREY};
  }
`;

const StyledContainer = styled.div<{
  $flexDirection?: 'row' | 'column';
  $justifyContent?: string;
  $alignItems?: string;
  $alignSelf?: string;
  $fontSize?: number;
  $gap?: number;
}>`
  display: flex;
  flex-direction: ${props => props.$flexDirection || 'column'};
  gap: ${props => props.$gap || 16}px;
  ${({ $fontSize }) => $fontSize && `font-size: ${$fontSize}px;`}
  ${({ $justifyContent }) =>
    $justifyContent && `justify-content: ${$justifyContent};`}
  ${({ $alignItems }) => $alignItems && `align-items: ${$alignItems};`}
  ${({ $alignSelf }) => $alignSelf && `align-self: ${$alignSelf};`}
`;

const StyledUpcomingChangesContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  align-self: stretch;
  flex-grow: 1;
`;

const StyledUpcomingChangesContentItemContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 7px;
  align-self: stretch;
`;

const ContactInfoItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  font-size: 16px;
  gap: 4px;
`;

const ActionButton = styled(Button)`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`;

const SubTitle = styled.span`
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 22px;
`;

const StyledPaymentButton = styled(Button)`
  --bb-blue: ${Color.PRIMARY};

  display: flex;
  padding: 0 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
  border: 1px solid var(--bb-blue);
  color: var(--bb-blue);
  font-size: 16px;
`;

const ServiceListingsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  margin-top: 1em;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const IconCircle = styled.div`
  width: 48px;
  height: 48px;
  min-width: 36px;
  background-color: ${Color.VEGETA_10};
  border-radius: 50%;
  padding: 10px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default PaymentConfirmationContent;
